import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Optional,
    SimpleChanges,
    ViewChild,
    isDevMode,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import type { EstimateRenderData } from '@encounter';
import type { LayoutEstimateRenderConfig } from '../../../models/layout-estimate-render-config';
import { PRINT_FONT_FACES } from './layout-fonts';
import { buildLayoutView, type LayoutView } from './layout-view';

export const LAYOUT_PRINTING_CLASS = 'layout-estimate-printing';
export const LAYOUT_PRINT_CONTAINER_CLASS = 'layout-estimate-print-container';
export const LAYOUT_PRINT_STYLE_ID = 'layout-estimate-print-style';

export const LAYOUT_PRINT_LABEL = 'Print Estimate';

const BASE_LOCALE = 'en_US';
const LAYOUT_KEY = /^estimate_layout_\S+$/;
const PRINT_ASSET_TIMEOUT_MS = 3000;


@Component({
    selector: 'estimate-layout-renderer',
    templateUrl: './layout-estimate-renderer.component.html',
    styleUrls: ['../../../scss/styles.scss', './layout-estimate-renderer.component.scss'],
    styles: [
        `
                     :host {
                         display: contents;
                     }
                 `,
    ],
    standalone: false
})
export class LayoutEstimateRendererComponent implements OnChanges, OnDestroy {
    @Input() public estimate: EstimateRenderData | undefined;
    @Input() public language: Record<string, Record<string, unknown>> | undefined;

    private viewport: ElementRef<HTMLElement> | undefined;

    @ViewChild('sheets', { static: false })
    private sheets: ElementRef<HTMLElement> | undefined;

    public config: LayoutEstimateRenderConfig | undefined;
    public view: LayoutView | undefined;
    public readonly printLabel = LAYOUT_PRINT_LABEL;
    /** Screen scale of the fixed sheet; 1 when it fits and always 1 in print. */
    public scale = 1;

    private printContainer: HTMLElement | undefined;
    private printStyle: HTMLStyleElement | undefined;
    private resizeObserver: ResizeObserver | undefined;
    private langChange: Subscription | undefined;

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly zone: NgZone,
        private readonly changeDetector: ChangeDetectorRef,
        @Optional() private readonly translate: TranslateService | null,
    ) {
        this.langChange = this.translate?.onLangChange.subscribe(() => this.build());
    }

    @ViewChild('viewport', { static: false })
    private set viewportRef(ref: ElementRef<HTMLElement> | undefined) {
        this.viewport = ref;
        this.observe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['estimate'] || changes['language']) {
            // GraphQL hosts nest the render config under estimateConfig; shopping passes the REST shape directly.
            this.config = (this.estimate?.estimateConfig?.estimate || this.estimate) as LayoutEstimateRenderConfig | undefined;
            this.build();
        }
    }

    /** An image that fails to load renders nothing, not a broken-image icon. */
    public hideImage(event: Event): void {
        (event.target as HTMLElement).style.display = 'none';
    }

    public showImage(event: Event): void {
        (event.target as HTMLElement).style.removeProperty('display');
    }

    public print(): void {
        void this.printWhenReady();
    }

    ngOnDestroy(): void {
        this.langChange?.unsubscribe();
        this.resizeObserver?.disconnect();
        this.document.defaultView?.removeEventListener('afterprint', this.cleanupPrint);
        this.cleanupPrint();
    }

    private build(): void {
        const layout = this.config?.layout;

        this.view = layout?.pages?.length ? buildLayoutView(layout, this.lookup) : undefined;

        if (isDevMode() && this.config?.variables?.layoutIssues?.length) {
            // Paths only (never values); for dev tooling, not for patients.
            console.warn('[estimate-layout] layoutIssues', this.config.variables.layoutIssues);
        }

        this.updateScale();
    }

    private readonly lookup = (key: string | undefined): string => {
        if (!key) {
            return '';
        }

        const language = (this.language ?? this.estimate?.estimateConfig?.language) as
            | Record<string, Record<string, unknown>>
            | undefined;
        const locale = this.translate?.currentLang || this.translate?.defaultLang || BASE_LOCALE;
        const short = locale.split(/[-_]/)[0];
        const table =
            language?.[locale] ??
            Object.entries(language ?? {}).find(([name]) => name.split(/[-_]/)[0] === short)?.[1] ??
            language?.[BASE_LOCALE];
        const hit = table?.[key] ?? language?.[BASE_LOCALE]?.[key];

        if (typeof hit === 'string') {
            return hit;
        }

        return LAYOUT_KEY.test(key) ? '' : key;
    };

    private observe(): void {
        this.resizeObserver?.disconnect();
        this.resizeObserver = undefined;

        const element = this.viewport?.nativeElement;

        if (!element || typeof ResizeObserver === 'undefined') {
            return;
        }

        this.resizeObserver = new ResizeObserver(() =>
            this.zone.run(() => {
                this.updateScale();
                this.changeDetector.markForCheck();
            }),
        );
        this.resizeObserver.observe(element);
    }

    private updateScale(): void {
        const available = this.viewport?.nativeElement.clientWidth ?? 0;
        const sheetWidth = this.view?.geometry.width ?? 0;

        this.scale = available > 0 && sheetWidth > 0 ? Math.min(1, available / sheetWidth) : 1;
    }

    private async printWhenReady(): Promise<void> {
        const root = this.sheets?.nativeElement;
        const view = this.document.defaultView;

        if (!root || !view || !this.view) {
            return;
        }

        this.cleanupPrint();

        const container = this.document.createElement('div');
        container.className = LAYOUT_PRINT_CONTAINER_CLASS;
        container.appendChild(this.printClone(root, this.view.geometry));
        this.document.body.appendChild(container);

        const style = this.document.createElement('style');
        style.id = LAYOUT_PRINT_STYLE_ID;
        style.textContent = printCss(this.config?.layout.page.size === 'a4' ? 'A4' : 'letter', this.view.geometry.width);
        this.document.head.appendChild(style);

        this.printContainer = container;
        this.printStyle = style;

        await this.assetsReady(container);

        // Cleaned up already (destroyed, or a second print started) while we waited.
        if (this.printContainer !== container) {
            return;
        }

        this.document.documentElement.classList.add(LAYOUT_PRINTING_CLASS);
        view.addEventListener('afterprint', this.cleanupPrint, { once: true });
        view.print();
    }

    private printClone(root: HTMLElement, geometry: { width: number; height: number }): HTMLElement {
        const clone = root.cloneNode(true) as HTMLElement;

        clone.querySelectorAll<HTMLElement>('.lx-scaler').forEach((scaler) => {
            scaler.style.width = `${geometry.width}px`;
            scaler.style.height = `${geometry.height}px`;
        });
        clone.querySelectorAll<HTMLElement>('.lx-sheet').forEach((sheet) => {
            sheet.style.transform = 'none';
            sheet.style.width = `${geometry.width}px`;
            sheet.style.height = `${geometry.height}px`;
        });

        return clone;
    }

    private async assetsReady(container: HTMLElement): Promise<void> {
        const fonts = this.document.fonts;
        const waits: Promise<unknown>[] = [];

        if (fonts) {
            waits.push(...PRINT_FONT_FACES.map((face) => fonts.load(face).catch(() => undefined)), fonts.ready);
        }

        for (const image of Array.from(container.querySelectorAll('img'))) {
            waits.push(image.decode ? image.decode().catch(() => undefined) : Promise.resolve());
        }

        await Promise.race([
            Promise.all(waits),
            new Promise((resolve) => setTimeout(resolve, PRINT_ASSET_TIMEOUT_MS)),
        ]);
    }

    private readonly cleanupPrint = (): void => {
        this.printContainer?.remove();
        this.printStyle?.remove();
        this.printContainer = undefined;
        this.printStyle = undefined;
        this.document.documentElement.classList.remove(LAYOUT_PRINTING_CLASS);
    };
}


function printCss(size: 'letter' | 'A4', pageWidth: number): string {
    const c = `.${LAYOUT_PRINT_CONTAINER_CLASS}`;

    return `
        .${LAYOUT_PRINT_CONTAINER_CLASS} { display: none; }
        @page { size: ${size}; margin: 0; }
        @media print {
            html.${LAYOUT_PRINTING_CLASS}, html.${LAYOUT_PRINTING_CLASS} body { width: ${pageWidth}px !important; min-width: 0 !important; max-width: ${pageWidth}px !important; height: auto !important; min-height: 0 !important; margin: 0 !important; padding: 0 !important; overflow: visible !important; position: static !important; transform: none !important; zoom: 1 !important; background: #fff !important; }
            html.${LAYOUT_PRINTING_CLASS} body > *:not(${c}) { display: none !important; }
            html.${LAYOUT_PRINTING_CLASS} ${c} { display: block; }
            ${c}, ${c} * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            ${c} .lx-sheets { gap: 0 !important; display: block !important; }
            ${c} { width: ${pageWidth}px; }
            ${c} .lx-scaler { break-after: page; page-break-after: always; }
            ${c} .lx-scaler:last-child { break-after: auto; page-break-after: auto; }
            ${c} .lx-sheet { transform: none !important; box-shadow: none !important; margin: 0 !important; }
        }
    `;
}
